/* LangChain connect snippets — one per (client × language).
 *
 * Contract anchor: "Getting Started — LangChain" (Notion, IG docs) — the
 * customer-facing guide these snippets must stay in step with.
 *
 * Wiring the gateway into LangChain is always the same three moves: base URL at
 * the gateway, the caller's own provider key unchanged, the AI21 headers added
 * on top. Only two things vary:
 *   - The base URL, per *dialect*. The Anthropic SDK appends the whole
 *     /v1/messages, so it gets the gateway root; the OpenAI SDK appends only
 *     /chat/completions, so it needs /v1 already in the base. Mismatching them
 *     is a 404, not an auth error.
 *   - Where the arguments live, per *language*. In Python `base_url` and
 *     `default_headers` are top-level on every chat model; in TypeScript the
 *     nesting is per class — `clientOptions` for Anthropic, `configuration`
 *     (which also carries `baseURL`) for OpenAI. A top-level `defaultHeaders`
 *     is silently ignored.
 *
 * The AI21 header block itself is byte-identical everywhere: the gateway picks
 * the upstream from the model id, so only a model id that doesn't name its
 * provider needs `x-upstream`.
 *
 * Every snippet stops at constructing the client. The reader already has a
 * working agent to call from, and a trailing demo invoke would be top-level
 * `await` in TypeScript — which doesn't compile in a CommonJS project.
 */

import type { CodeBlockTab } from "../../design-system";
import {
  ANTHROPIC_KEY_PLACEHOLDER,
  OPENAI_KEY_PLACEHOLDER,
  type SnippetInputs,
} from "./snippetContext";

/** The chat model the snippet constructs: a provider class (`ChatAnthropic` /
 *  `ChatOpenAI`), or LangChain's provider-agnostic initializer
 *  (`init_chat_model` / `initChatModel`) — each in a Claude and a GPT flavor,
 *  since the provider is what decides the base URL and argument nesting. */
export type LangChainClient =
  "chat-anthropic" | "chat-openai" | "init-anthropic" | "init-openai";

/** Pre-selected client: Claude via `ChatAnthropic`, the console's default. */
export const DEFAULT_LANGCHAIN_CLIENT: LangChainClient = "chat-anthropic";

export interface LangChainClientOption {
  value: LangChainClient;
  label: string;
  /** Heading of the picker <optgroup> this option belongs to. */
  group: string;
}

/** The client picker's options, grouped by how the model is constructed. */
export const LANGCHAIN_CLIENTS: LangChainClientOption[] = [
  {
    value: "chat-anthropic",
    label: "ChatAnthropic — Claude",
    group: "Provider class",
  },
  {
    value: "chat-openai",
    label: "ChatOpenAI — GPT & OpenAI-compatible",
    group: "Provider class",
  },
  {
    value: "init-anthropic",
    label: "Claude (Anthropic)",
    group: "init_chat_model / initChatModel",
  },
  {
    value: "init-openai",
    label: "GPT (OpenAI)",
    group: "init_chat_model / initChatModel",
  },
];

/** The provider wire format a client speaks — what actually shapes the snippet. */
type Dialect = "anthropic" | "openai";

interface DialectSpec {
  /** Example model id; the gateway pins no model, so any id the provider serves works. */
  model: string;
  /** Placeholder for the caller's own provider key (never the AI21 key). */
  keyPlaceholder: string;
  /** Trailing comment on the api_key line. */
  keyNote: string;
  /** Appended to the gateway URL so the path the SDK builds lands on /v1/…. */
  basePath: string;
  /** Trailing comment on the base-URL line; empty where the URL needs no gloss. */
  baseUrlNote: string;
  /** x-upstream value, or null when the model id already names the provider. */
  upstream: string | null;
  /** Trailing comment on the x-upstream line (only used when `upstream` is set). */
  upstreamNote: string;
  python: { module: string; cls: string };
  typescript: { module: string; cls: string };
}

const DIALECTS: Record<Dialect, DialectSpec> = {
  anthropic: {
    model: "claude-sonnet-4-5",
    keyPlaceholder: ANTHROPIC_KEY_PLACEHOLDER,
    keyNote: "unchanged — still your own Anthropic key",
    basePath: "",
    // Without this the missing /v1 reads as a typo and gets "corrected" into a 404.
    baseUrlNote: "the Anthropic SDK appends /v1 itself",
    // A "claude-" id routes itself, and Anthropic is also the fallback.
    upstream: null,
    upstreamNote: "",
    python: { module: "langchain_anthropic", cls: "ChatAnthropic" },
    typescript: { module: "@langchain/anthropic", cls: "ChatAnthropic" },
  },
  openai: {
    model: "gpt-5.5",
    keyPlaceholder: OPENAI_KEY_PLACEHOLDER,
    keyNote: "unchanged — still your own OpenAI key",
    basePath: "/v1",
    baseUrlNote: "",
    upstream: "openai",
    upstreamNote: 'optional — the gateway reads a "gpt-" id as OpenAI',
    python: { module: "langchain_openai", cls: "ChatOpenAI" },
    typescript: { module: "@langchain/openai", cls: "ChatOpenAI" },
  },
};

function dialectOf(client: LangChainClient): Dialect {
  return client.endsWith("openai") ? "openai" : "anthropic";
}

function usesInitializer(client: LangChainClient): boolean {
  return client.startsWith("init-");
}

/** The AI21 header block, indented for its nesting level. Same three headers
 *  (plus x-upstream where the model id needs it) in every snippet. */
function headerLines(
  inputs: SnippetInputs,
  spec: DialectSpec,
  indent: string,
  commentLead: string,
): string[] {
  const lines = [
    `${indent}"x-ai21-key": "${inputs.ai21Key}",`,
    `${indent}"x-agent-id": "${inputs.agentId}",`,
    `${indent}"x-ai21-user-id": "${inputs.userId}",`,
  ];
  if (spec.upstream)
    lines.push(
      `${indent}"x-upstream": "${spec.upstream}",  ${commentLead} ${spec.upstreamNote}`,
    );

  return lines;
}

/* Python: every chat model takes base_url and default_headers as top-level
 * constructor arguments, so only the import + opening line differ. */
function pythonSnippet(client: LangChainClient, inputs: SnippetInputs): string {
  const dialect = dialectOf(client);
  const spec = DIALECTS[dialect];

  const head = usesInitializer(client)
    ? [
        "from langchain.chat_models import init_chat_model",
        "",
        "llm = init_chat_model(",
        `    "${spec.model}",`,
        // Explicit model_provider: the initializer otherwise infers it from the
        // model name.
        `    model_provider="${dialect}",`,
      ]
    : [
        `from ${spec.python.module} import ${spec.python.cls}`,
        "",
        `llm = ${spec.python.cls}(`,
        `    model="${spec.model}",`,
      ];

  const baseUrlNote = spec.baseUrlNote ? `  # ${spec.baseUrlNote}` : "";

  return [
    ...head,
    `    base_url="${inputs.gatewayUrl}${spec.basePath}",${baseUrlNote}`,
    `    api_key="${spec.keyPlaceholder}",  # ${spec.keyNote}`,
    "    default_headers={",
    ...headerLines(inputs, spec, "        ", "#"),
    "    },",
    ")",
  ].join("\n");
}

/* TypeScript: apiKey stays top-level, but the base URL and headers nest per
 * class — anthropicApiUrl + clientOptions.defaultHeaders for Anthropic,
 * configuration.{baseURL,defaultHeaders} for OpenAI. */
function typescriptSnippet(
  client: LangChainClient,
  inputs: SnippetInputs,
): string {
  const dialect = dialectOf(client);
  const spec = DIALECTS[dialect];
  const baseUrl = `${inputs.gatewayUrl}${spec.basePath}`;
  const apiKeyLine = `  apiKey: "${spec.keyPlaceholder}", // ${spec.keyNote}`;
  const headers = headerLines(inputs, spec, "      ", "//");

  const head = usesInitializer(client)
    ? [
        'import { initChatModel } from "langchain";',
        "",
        `const llm = await initChatModel("${spec.model}", {`,
        `  modelProvider: "${dialect}",`,
      ]
    : [
        `import { ${spec.typescript.cls} } from "${spec.typescript.module}";`,
        "",
        `const llm = new ${spec.typescript.cls}({`,
        `  model: "${spec.model}",`,
      ];

  const body =
    dialect === "anthropic"
      ? [
          `  anthropicApiUrl: "${baseUrl}", // ${spec.baseUrlNote}`,
          apiKeyLine,
          "  clientOptions: {",
          "    defaultHeaders: {",
          ...headers,
          "    },",
          "  },",
        ]
      : [
          apiKeyLine,
          "  configuration: {",
          `    baseURL: "${baseUrl}",`,
          "    defaultHeaders: {",
          ...headers,
          "    },",
          "  },",
        ];

  return [...head, ...body, "});"].join("\n");
}

/** The language tabs for one client — python first, then typescript. */
export function langchainTabs(
  client: LangChainClient,
  inputs: SnippetInputs,
): CodeBlockTab[] {
  return [
    { label: "python", code: pythonSnippet(client, inputs) },
    { label: "typescript", code: typescriptSnippet(client, inputs) },
  ];
}
